import filter from 'lodash/filter';
import includes from 'lodash/includes';
import forEach from 'lodash/forEach';
import find from 'lodash/find';

import {
  useGetCryptoListQuery,
  useGetCurrentListQuery,
  useGetDrawerMenuQuery,
  useGetSidebarListQuery,
  useGetTablePriceQuery
} from '@/services/api';
import cryptoNormalizer from '@/utils/cryptoNormalizer';

export type P2pType = {
  id: number;
  symbol: string;
  marketId: number;
  marketBase: string;
  marketQuote: string;
  status: string;
  description: string;
  created: string;
  updated: string;
  min: string;
  max: string;
  tick: number;
  basePrecision: number;
  quotePrecision: number;
  commissionPercentage: number;
  minLotSize: number;
  minNotional: string;
  pricePrecision: number;
  arrangement: number;
  imagePath: string;
  specialTips: string;
  softTips: string;
  marketBaseFa: string;
  marketQuoteFa: string;
  price: string;
  priceLastUpdate: string;
  priceChange: string;
  externalChartSource: string;
  rabexChartStatus: string;
  total: string;
  volume: string;
  highPrice?: string;
  lowPrice?: string;
  universal?: string;
  midPrice?: string;
};

export type OtcType = {
  id: number;
  marketSymbol: string;
  marketBase: string;
  marketQuote: string;
  price: string;
  buyStatus: string;
  sellStatus: string;
  sellDescription: string;
  buyDescription: string;
  created: string;
  updated: string;
  minSell: string;
  maxSell: string;
  minBuy: string;
  maxBuy: string;
  tick: string;
  defaultQuoteValue: string;
  defaultBaseValue: string;
  basePrecision: number;
  quotePrecision: number;
  buyPrice: string;
  sellPrice: string;
  marketBaseFa: string;
  marketQuoteFa: string;
  commissionPercentage: number;
  priceLastUpdate: string;
  arrangement: number;
  priceChange: string;
  total?: string;
  volume?: string;
  highPrice?: string;
  lowPrice?: string;
  universal?: string;
  midPrice?: string;
};

export type MarketInfo = {
  curr_a: string;
  curr_b: string;
  curr_suffix: string;
  high: string;
  low: string;
  lq: string;
  marketcap: string;
  name: string;
  name_cn: string;
  name_en: string;
  no: 1;
  p_rate: 1;
  pair: string;
  rate: string;
  rate_percent: string;
  supply: string;
  symbol: string;
  trend: string;
  vol_a: string;
  vol_b: string;
};

export type CryptoType = {
  id: number;
  symbol: string;
  base: string;
  quote: string;
  baseFa: string;
  quoteFa: string;
  otc?: OtcType;
  p2p?: P2pType;
  chartUrl?: string;
  logoUrl?: string;
  arrangement: number;
  marketInfo?: MarketInfo;
};

export type keyType = 'p2p' | 'otc';

export type useGetCryptoPropsType = {
  topGainers?: { side: keyType; count?: number; quote?: string };
  topLosers?: { side: keyType; count?: number; quote?: string };
  trends?: { side: keyType; count?: number; quote?: string };
  count?: number;
  recentlyAdded?: number;
  homeTable?: boolean;
  shitcoinTable?: boolean;
  p2p?: { side: keyType; count?: number; quote?: string };
  otc?: { side: keyType; count?: number; quote?: string };
  get?: { side: keyType; filter?: string[]; count?: number };
};

export type KeyNameType =
  | 'getTablePrice'
  | 'getCryptoList'
  | 'getDrawerMenu'
  | 'getSidebarList'
  | 'getCurrentList';

export type useGetCryptoResultType = {
  topGainers: CryptoType[];
  topLosers: CryptoType[];
  trends: CryptoType[];
  recentlyAdded: CryptoType[];
  tablePrice: CryptoType[];
  shitcoinTablePrice: CryptoType[];
  result: CryptoType[];
  list: CryptoType[];
  p2p: CryptoType[];
  otc: CryptoType[];
};

export const ascending = (arr: any[], key: string, alter: string) =>
  arr.sort((a, b) => {
    const first = !a[key] && alter ? a[alter][key] : a[key];
    const second = !b[key] && alter ? b[alter][key] : b[key];
    return Number(`${first}`.replace(/,/g, '')) >
      Number(`${second}`.replace(/,/g, ''))
      ? 1
      : -1;
  });

export const descending = (arr: any[], key: string, alter: string) =>
  arr.sort((a, b) => {
    const first = !a[key] && alter ? a[alter][key] : a[key];
    const second = !b[key] && alter ? b[alter][key] : b[key];
    return Number(`${first}`.replace(/,/g, '')) >
      Number(`${second}`.replace(/,/g, ''))
      ? -1
      : 1;
  });

let recently: CryptoType[] = [];
let tablePrice: CryptoType[] = [];
let shitcoinTablePrice: CryptoType[] = [];
let result: CryptoType[] = [];
let topGainersRes: CryptoType[] = [];
let topLosersRes: CryptoType[] = [];

const useGetCrypto = (
  props: useGetCryptoPropsType,
  keyName: KeyNameType,
  keyValue?: any
): useGetCryptoResultType => {
  const {
    topGainers,
    topLosers,
    trends,
    recentlyAdded,
    get,
    count,
    p2p,
    otc,
    homeTable,
    shitcoinTable
  } = props;

  const selector = {
    getTablePrice: useGetTablePriceQuery,
    getCryptoList: useGetCryptoListQuery,
    getDrawerMenu: useGetDrawerMenuQuery,
    getSidebarList: useGetSidebarListQuery,
    getCurrentList: useGetCurrentListQuery
  };
  const { data } = selector[keyName](keyValue);

  const list = cryptoNormalizer(data?.data || []);

  if (recentlyAdded) {
    const items = require(`../../public/json/market/recentlyAdded.json`);
    recently = filter(list, (item: CryptoType) => {
      let source: keyType = items[item.symbol];
      return !!item[source];
    });
  }
  if (homeTable) {
    const items = require(`../../public/json/fetchData/tablePrice.json`);
    tablePrice = filter(list, (item: CryptoType) => {
      let source: keyType = items[item.symbol];
      return !!item[source];
    });
  }
  if (shitcoinTable && shitcoinTablePrice.length === 0) {
    const items = require(`../../public/json/shitcoin/info.json`);
    forEach(items.list, item => {
      let crypto = find(
        list,
        mi => mi.base === item.base && mi.quote === item.quote
      );
      if (!!crypto?.[item.source as keyType])
        shitcoinTablePrice.push(crypto as CryptoType);
    });
  }

  if (get?.side) {
    const assetList = filter(
      list,
      (item: CryptoType) =>
        includes(get.filter, item.symbol) && !!item[get.side]
    );
    result = ascending(assetList, 'arrangement', get.side);
  }

  if (topGainers) {
    const filtered = filter(
      list,
      (item: CryptoType) =>
        !!item[topGainers.side] &&
        (topGainers?.quote ? item.quote === topGainers.quote : true)
    );
    topGainersRes = descending(filtered, 'priceChange', topGainers.side);
  }

  if (topLosers) {
    const filtered = filter(
      list,
      (item: CryptoType) =>
        !!item[topLosers.side] &&
        (topLosers?.quote ? item.quote === topLosers.quote : true)
    );
    topLosersRes = ascending(filtered, 'priceChange', topLosers.side);
  }

  let trendsRes: CryptoType[] = [];
  if (trends) {
    const filtered = filter(
      list,
      (item: CryptoType) =>
        !!item[trends.side] &&
        (trends?.quote ? item.quote === trends.quote : true)
    );
    trendsRes = descending(filtered, 'volume', 'p2p');
  }

  let p2pRes: CryptoType[] = [];
  if (p2p?.side) {
    const p2pList = filter(
      list,
      (item: CryptoType) =>
        !!item['p2p'] && (p2p?.quote ? item.quote === p2p.quote : true)
    );
    p2pRes = ascending(p2pList, 'arrangement', p2p.side);
  }

  let otcRes: CryptoType[] = [];
  if (otc?.side) {
    const otcList = filter(
      list,
      (item: CryptoType) =>
        !!item['otc'] && (otc?.quote ? item.quote === otc.quote : true)
    );
    otcRes = ascending(otcList, 'arrangement', otc.side);
  }

  return {
    topGainers:
      topGainers?.count && topGainers.count > 0
        ? topGainersRes.slice(0, topGainers.count)
        : topGainersRes,
    topLosers:
      topLosers?.count && topLosers.count > 0
        ? topLosersRes.slice(0, topLosers.count)
        : topLosersRes,
    trends:
      trends?.count && trends.count > 0
        ? trendsRes.slice(0, trends.count)
        : trendsRes,
    recentlyAdded:
      recentlyAdded && recentlyAdded > 0
        ? recently.slice(0, recentlyAdded)
        : recently,
    tablePrice,
    shitcoinTablePrice,
    result,
    list: count && count > 0 ? list.slice(0, count) : list,
    p2p: p2p?.count && p2p?.count > 0 ? p2pRes.slice(0, p2p.count) : p2pRes,
    otc: otc?.count && otc?.count > 0 ? otcRes.slice(0, otc.count) : otcRes
  };
};

export default useGetCrypto;
